import React, { Dispatch, SetStateAction, useRef, useState } from "react";
import * as ajaxEndpoints from "../../../../../../api/ajax-endpoints";
import ButtonLoader from "../../../../../../UI/Loaders/Loaders";
import { postData } from "../../../../../../newApis/apiMethods";
import { appInsights } from "../../../../../../config/appInsights";
import { errorHandler } from "../../../../../../helpers/errorHandler";
import { useQueryCache } from "react-query";
import { IMandatePreviewDetails } from "./DirectDebit";
import styles from "./DirectDebitStyles.module.scss";

interface ImandatePrompt {
  currentBankAccount: string;
  setCurrentBankAccount: Dispatch<SetStateAction<string>>;
  loan_id: number;
  loan_ref: string;
  setSuccess: Dispatch<SetStateAction<string>>;
  setError: Dispatch<SetStateAction<string | null>>;
  scrollToTop: () => void;
  mandatePreviewLoading: boolean;
  mandatePreviewError: string | null;
  mandatePreviewSuccess: string | null;
  mandatePreviewResponseData: IMandatePreviewDetails | null;
  mandateTypeId: string;
  setMandateTypeId: Dispatch<SetStateAction<string>>;
  isBusinessApplication?: boolean;
  refetchMandateList: () => void;
}
const DirectDebitMandateModalPrompt = ({
  currentBankAccount,
  setCurrentBankAccount,
  mandatePreviewLoading,
  mandatePreviewError,
  mandatePreviewSuccess,
  loan_ref,
  setSuccess,
  setError,
  scrollToTop,
  mandatePreviewResponseData,
  mandateTypeId,
  setMandateTypeId,
  isBusinessApplication,
  refetchMandateList,
}: ImandatePrompt) => {
  const [loading, setLoading] = useState(false);
  const dismissModalRef = useRef<HTMLButtonElement | null>(null);
  const queryCache = useQueryCache();

  const sendMandate = async () => {
    setLoading(true);
    setError(null);
    setSuccess("");
    const reqBody = {
      loanId: mandatePreviewResponseData?.loanId,
      amount: mandatePreviewResponseData?.amount,
      customerAccountNumber: mandatePreviewResponseData?.payerAccountNumber,
      mandateTypeId,
    };

    let response;

    try {
      if (isBusinessApplication) {
        response = await postData(
          ajaxEndpoints.INITIATE_PAPER_MANDATE,
          reqBody,
        );
      } else {
        response = await postData(ajaxEndpoints.GET_DIRECT_DEBIT_MANDATE_LIST, {
          ...mandatePreviewResponseData,
          mandateTypeId: 1,
        });
      }

      setLoading(false);
      setSuccess(response?.message);

      setTimeout(() => {
        setSuccess("");
      }, 6000);
      scrollToTop();
      queryCache.invalidateQueries("getDirectDebitAccountMandate");
      refetchMandateList();

      if (dismissModalRef && dismissModalRef.current) {
        dismissModalRef?.current?.click();
      }
      setCurrentBankAccount("");
      setMandateTypeId("");
      setTimeout(() => {
        setSuccess("");
      }, 5000);
    } catch (error) {
      appInsights.trackException({
        //@ts-ignore
        exception: error,
        properties: { fileName: "DirectDebitMandateModalPrompt.tsx" },
      });
      setLoading(false);
      if (dismissModalRef && dismissModalRef.current) {
        dismissModalRef.current.click();
      }
      scrollToTop();

      setError(errorHandler(error));

      setTimeout(() => {
        setError("");
      }, 5000);
    }
  };

  return (
    <div
      className="modal"
      id="directDebitMandateSubmit"
      tabIndex={-1}
      role="dialog"
    >
      <div className="mt-4 modal-dialog" role="document">
        <div className="modal-content p-3">
          {mandatePreviewSuccess && (
            <p className="alert alert-success text-align-center py-2">
              {mandatePreviewSuccess}
            </p>
          )}
          {mandatePreviewError && (
            <p className="alert alert-danger text-align-center py-2">
              {mandatePreviewError}
            </p>
          )}
          <div className="d-flex justify-content-between ">
            <button
              type="button"
              className="close"
              data-dismiss="modal"
              aria-label="Close"
              ref={dismissModalRef}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body mt-3 text-center">
            {mandatePreviewLoading && (
              <div className="d-flex justify-content-center align-items-center">
                <p className="text-danger">Loading details....</p>
              </div>
            )}
            {mandatePreviewResponseData && (
              <div className={styles.content}>
                <p>Are you sure you want to submit the selected Mandate?</p>

                <div className={`${styles["accordion-content"]} my-2`}>
                  <h6>Mandate Information</h6>{" "}
                  <ul className="p-0 pl-2">
                    <li className="">
                      <div className="d-flex justify-content-between align-items-center">
                        <small>Direct Debit Provider:</small>
                        <small className="mr-4">
                          {" "}
                          {mandatePreviewResponseData?.directDebitProvider ??
                            "Nil"}
                        </small>
                      </div>
                    </li>
                    <li className="">
                      <div className="d-flex justify-content-between align-items-center">
                        <small>Payer BVN:</small>
                        <small className="mr-4">
                          {" "}
                          {mandatePreviewResponseData?.payerBvn ?? "Nil"}
                        </small>
                      </div>
                    </li>
                    <li className="">
                      <div className="d-flex justify-content-between align-items-center">
                        <small>Payer Account Number:</small>
                        <small className="mr-4">
                          {" "}
                          {mandatePreviewResponseData?.payerAccountNumber}
                        </small>
                      </div>
                    </li>
                    <li className="">
                      <div className="d-flex justify-content-between align-items-center">
                        <small>Payer Bank:</small>
                        <small className="mr-4">
                          {" "}
                          {mandatePreviewResponseData?.payerbankName}
                        </small>
                      </div>
                    </li>
                    <li className="">
                      <div className="d-flex justify-content-between align-items-center">
                        <small>Amount:</small>
                        <small className="mr-4">
                          {" "}
                          {mandatePreviewResponseData?.amount}
                        </small>
                      </div>
                    </li>
                    <li className="">
                      <div className="d-flex justify-content-between align-items-center">
                        <small>Currency:</small>
                        <small className="mr-4">
                          {" "}
                          {mandatePreviewResponseData?.currency}
                        </small>
                      </div>
                    </li>
                    <li className="">
                      <div className="d-flex justify-content-between align-items-center">
                        <small>Start Date:</small>
                        <small className="mr-4">
                          {" "}
                          {mandatePreviewResponseData?.startDate}
                        </small>
                      </div>
                    </li>
                    <li className="">
                      <div className="d-flex justify-content-between align-items-center">
                        <small>End Date:</small>
                        <small className="mr-4">
                          {" "}
                          {mandatePreviewResponseData?.endDate}
                        </small>
                      </div>
                    </li>
                    <li className="">
                      <div className="d-flex justify-content-between align-items-center">
                        <small>Frequency :</small>
                        <small className="mr-4">
                          {" "}
                          {mandatePreviewResponseData?.frequencyName}
                        </small>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className={styles.footer_wrapper}>
                  <div className={styles.button_container}>
                    <div className={styles.btns_innerWrapper}>
                      <button
                        type="button"
                        className="btn zoom-element btn login-btn"
                        disabled={loading}
                        onClick={sendMandate}
                      >
                        Submit Mandate
                        {loading && <ButtonLoader />}
                      </button>

                      <button
                        type="button"
                        className="btn zoom-element "
                        data-dismiss="modal"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DirectDebitMandateModalPrompt;
